import type { PeopleSheet, RequestStatus } from './enums.js';
import { SEED_JOB_LIMITS } from './catalog.js';
import { MOCK_PEOPLE, VENUE_SLOTS, type MockPerson } from './mockDirectory.js';
import type { TestVenue } from './testVenues.js';

/**
 * Sample requests for `pnpm db:seed` and the in-browser demo store (spec 6
 * seed). One source for both. Synthetic: names are fictional and every date
 * is relative to `asOf`.
 */
export interface DemoRequestSeed {
  legalEntityId: string;
  venueName: string;
  status: RequestStatus;
  currentGate: number;
  requesterUpn: string;
  targetGoLive: string;
  answers: Record<string, unknown>;
  peopleImport: {
    status: 'Valid';
    rows: Array<{ sheet: PeopleSheet; data: Record<string, unknown> }>;
  } | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const isoDate = (asOf: Date, days: number): string =>
  new Date(asOf.getTime() + days * DAY_MS).toISOString().slice(0, 10);

/**
 * The finance director's position in the target environment (position 000613, job Finance-director,
 * reporting to CFO 000612 in the sales-cd9 sandbox, seen 2026-10-23; VERIFY per environment). Each
 * venue's GL accountant position reports to it.
 */
export const FINANCE_DIRECTOR_POSITION_ID = '000613';

/**
 * A second position for the venue's first person (the GM): GL accountant, who enters and submits
 * general journals. It is their primary position and reports to the finance director, so a
 * journal's managerial-hierarchy approval starts there. The GM position stays theirs (not primary).
 */
export const GL_ACCOUNTANT_SLOT = {
  suffix: 'GLACCT',
  title: 'GL accountant',
  jobId: 'GL-ACCOUNTANT',
  reportsTo: FINANCE_DIRECTOR_POSITION_ID,
  roles: ['LEDGERACCOUNTANT'],
} as const;

const SLOTS = [
  {
    suffix: 'GM',
    title: 'General manager',
    jobId: 'VENUE-GM',
    reportsTo: 'REG-FIN-01',
    roles: ['LEDGERACCOUNTINGMANAGER'],
  },
  {
    suffix: 'FM',
    title: 'Finance manager',
    jobId: 'FIN-MGR',
    reportsTo: 'GM',
    roles: ['VENDINVOICEACCOUNTSPAYABLEMANAGER', 'LEDGERACCOUNTINGMANAGER'],
  },
  { suffix: 'BUY', title: 'Buyer', jobId: 'BUYER', reportsTo: 'FM', roles: ['VENDPURCHASINGAGENT'] },
  {
    suffix: 'AP',
    title: 'AP clerk',
    jobId: 'AP-CLERK',
    reportsTo: 'FM',
    roles: ['VENDINVOICEACCOUNTSPAYABLECLERK'],
  },
  {
    suffix: 'PM',
    title: 'Project manager',
    jobId: 'PROJ-MGR',
    reportsTo: 'GM',
    roles: ['PROJPROJECTMANAGER'],
  },
] as const;

/**
 * A complete people import for a new venue. Staffed by `staff` in slot order (GM, finance
 * manager, buyer, AP clerk, project manager), or by the first unassigned directory users.
 */
export function demoPeopleRows(
  companyId: string,
  asOf: Date,
  staff?: readonly MockPerson[],
  templateCompany = 'USMF',
): NonNullable<DemoRequestSeed['peopleImport']>['rows'] {
  const hires = staff ?? MOCK_PEOPLE.filter((p) => p.placement === null && p.enabled).slice(0, SLOTS.length);
  // V045 → 045001…; any other company (T-numbers) keeps its letter so T002 never collides with V002.
  const num = companyId.startsWith('V') ? companyId.slice(1) : companyId;
  const rows: NonNullable<DemoRequestSeed['peopleImport']>['rows'] = [];
  SLOTS.forEach((slot, i) => {
    const p = hires[i];
    if (!p) return;
    const positionId = `${companyId}-${slot.suffix}`;
    const personnelNumber = `${num}${String(i + 1).padStart(3, '0')}`;
    rows.push({
      sheet: 'users',
      data: { upn: p.upn, displayName: `${p.firstName} ${p.lastName}`, email: p.upn },
    });
    rows.push({
      sheet: 'workers',
      data: {
        personnelNumber,
        firstName: p.firstName,
        lastName: p.lastName,
        upn: p.upn,
        employmentStart: isoDate(asOf, 14),
      },
    });
    rows.push({
      sheet: 'positions',
      data: {
        positionId,
        title: slot.title,
        jobId: slot.jobId,
        reportsToPositionId:
          slot.reportsTo === 'REG-FIN-01' ? 'REG-FIN-01' : `${companyId}-${slot.reportsTo}`,
      },
    });
    const glAccountant = i === 0;
    rows.push({
      sheet: 'assignments',
      data: { positionId, personnelNumber, startDate: isoDate(asOf, 14), primary: !glAccountant },
    });
    for (const roleId of slot.roles) rows.push({ sheet: 'roles', data: { upn: p.upn, roleId } });
    if (glAccountant) {
      const gl = GL_ACCOUNTANT_SLOT;
      const glPositionId = `${companyId}-${gl.suffix}`;
      rows.push({
        sheet: 'positions',
        data: {
          positionId: glPositionId,
          title: gl.title,
          jobId: gl.jobId,
          reportsToPositionId: gl.reportsTo,
        },
      });
      rows.push({
        sheet: 'assignments',
        data: { positionId: glPositionId, personnelNumber, startDate: isoDate(asOf, 14), primary: true },
      });
      for (const roleId of gl.roles) rows.push({ sheet: 'roles', data: { upn: p.upn, roleId } });
    }
  });
  const jobs = new Set<string>([...SLOTS.map((s) => s.jobId), GL_ACCOUNTANT_SLOT.jobId]);
  for (const l of SEED_JOB_LIMITS.filter((l) => jobs.has(l.jobId))) {
    rows.push({ sheet: 'approvalLimits', data: { ...l } });
  }
  const personnel = (slot: (typeof SLOTS)[number]['suffix']): string | undefined => {
    const i = SLOTS.findIndex((s) => s.suffix === slot);
    return hires[i] ? `${num}${String(i + 1).padStart(3, '0')}` : undefined;
  };
  rows.push(...demoMasterDataRows(companyId, asOf, personnel('PM'), personnel('FM'), templateCompany));
  return rows;
}

/**
 * Opening customers, vendors, project contracts and projects for a new venue. Fictional names;
 * groups are ones every template company has (the first project groups, so LTPC works too);
 * dates relative to `asOf`.
 */
export function demoMasterDataRows(
  companyId: string,
  asOf: Date,
  managerPersonnelNumber?: string,
  controllerPersonnelNumber?: string,
  templateCompany = 'USMF',
): NonNullable<DemoRequestSeed['peopleImport']>['rows'] {
  if (templateCompany === '1100')
    return legendsMasterDataRows(companyId, asOf, managerPersonnelNumber, controllerPersonnelNumber);
  const start = isoDate(asOf, 14);
  const end = isoDate(asOf, 14 + 364);
  const people = { managerPersonnelNumber, controllerPersonnelNumber };
  const project = (
    n: number,
    name: string,
    projectType: string,
    projectGroup: string,
    extra: Record<string, unknown> = {},
  ) => ({
    sheet: 'projects' as const,
    data: {
      projectId: `${companyId}-P${String(n).padStart(3, '0')}`,
      name,
      projectType,
      projectGroup,
      ...people,
      startDate: start,
      endDate: end,
      ...extra,
    },
  });
  return [
    {
      sheet: 'customers',
      data: {
        customerAccount: 'VC-0001',
        name: 'Northbank Concert Partners',
        customerGroup: 'EVENTS',
        currency: 'USD',
        email: 'billing@northbank.example',
        paymentTerms: 'NET30',
      },
    },
    {
      sheet: 'customers',
      data: {
        customerAccount: 'VC-0002',
        name: 'Tidewater Beverage Company',
        customerGroup: 'SPONSOR',
        currency: 'USD',
        email: 'partnerships@tidewater.example',
        paymentTerms: 'NET30',
      },
    },
    {
      sheet: 'vendors',
      data: {
        vendorAccount: 'VV-0001',
        name: 'Keystone Event Staffing',
        vendorGroup: 'STAFFING',
        currency: 'USD',
        paymentTerms: 'NET30',
      },
    },
    {
      sheet: 'vendors',
      data: {
        vendorAccount: 'VV-0002',
        name: 'Riverside Linen Service',
        vendorGroup: 'SERVICES',
        currency: 'USD',
        paymentTerms: 'NET30',
      },
    },
    {
      sheet: 'vendors',
      data: {
        vendorAccount: 'VV-0003',
        name: 'Copperleaf Catering Supply',
        vendorGroup: 'FB',
        currency: 'USD',
        paymentTerms: 'NET45',
      },
    },
    {
      sheet: 'projectContracts',
      data: {
        contractId: `${companyId}-PC01`,
        name: 'Naming rights and sponsorship',
        customerAccount: 'VC-0002',
      },
    },
    {
      sheet: 'projectContracts',
      data: { contractId: `${companyId}-PC02`, name: 'Fall concert series', customerAccount: 'VC-0001' },
    },
    project(1, 'Venue operations overhead', 'Internal', 'INTERNAL'),
    project(2, 'Parking operations', 'Cost', 'INTERNAL'),
    project(3, 'Food and beverage, in-house', 'Cost', 'INTERNAL'),
    project(4, 'Box office and ticketing', 'Cost', 'INTERNAL'),
    project(5, 'Naming rights and sponsorship', 'FixedPrice', 'EVENT-FP', {
      contractId: `${companyId}-PC01`,
    }),
    project(6, 'Fall concert series', 'TimeAndMaterial', 'EVENT-TM', { contractId: `${companyId}-PC02` }),
    project(7, 'Fall concert series - opening night', 'TimeAndMaterial', 'EVENT-TM', {
      contractId: `${companyId}-PC02`,
      parentProjectId: `${companyId}-P006`,
      startDate: isoDate(asOf, 30),
      endDate: isoDate(asOf, 31),
    }),
  ];
}

/**
 * Opening master data shaped like Legends' template company 1100 (TRN, queried 2026-10-27): its
 * customer groups (CG100 Venue Owner, CG150 Strategic Partner), vendor groups (VG120 Food & Beverage
 * Partner, VG140 Resource Partner - Temp Venue Ops, VG200 Venue Operation Partners - Non-Labor),
 * payment terms (Net30 customers, Net60 vendors) and its venue project structure: a time and
 * material header (VEN_MGMT) under the owner's management contract, with subprojects for the
 * management fixed fee (MGMT_FF), incentive fee (MGMT_KPI) and billing adjustments (BILL_ADJ),
 * plus an internal opex project (OPEX_INT). Names are fictional; dates relative to `asOf`.
 */
function legendsMasterDataRows(
  companyId: string,
  asOf: Date,
  managerPersonnelNumber?: string,
  controllerPersonnelNumber?: string,
): NonNullable<DemoRequestSeed['peopleImport']>['rows'] {
  const start = isoDate(asOf, 14);
  const end = isoDate(asOf, 14 + 364);
  const people = { managerPersonnelNumber, controllerPersonnelNumber };
  const header = `${companyId}-P001`;
  const contract = `${companyId}-PC01`;
  const project = (id: string, name: string, projectType: string, projectGroup: string, extra = {}) => ({
    sheet: 'projects' as const,
    data: {
      projectId: id,
      name,
      projectType,
      projectGroup,
      ...people,
      startDate: start,
      endDate: end,
      ...extra,
    },
  });
  const customer = (n: number, name: string, customerGroup: string, email: string) => ({
    sheet: 'customers' as const,
    data: {
      customerAccount: `VC-000${n}`,
      name,
      customerGroup,
      currency: 'USD',
      email,
      paymentTerms: 'Net30',
    },
  });
  const vendor = (n: number, name: string, vendorGroup: string) => ({
    sheet: 'vendors' as const,
    data: { vendorAccount: `VV-000${n}`, name, vendorGroup, currency: 'USD', paymentTerms: 'Net60' },
  });
  return [
    customer(1, 'Lakeshore Venue Authority', 'CG100', 'ap@lakeshorevenues.example'),
    customer(2, 'Tidewater Beverage Company', 'CG150', 'partnerships@tidewater.example'),
    vendor(1, 'Keystone Event Staffing', 'VG140'),
    vendor(2, 'Riverside Linen Service', 'VG200'),
    vendor(3, 'Copperleaf Catering Supply', 'VG120'),
    {
      sheet: 'projectContracts',
      data: { contractId: contract, name: 'Venue management agreement', customerAccount: 'VC-0001' },
    },
    project(header, `${companyId} Venue Management Project Header`, 'TimeAndMaterial', 'VEN_MGMT', {
      contractId: contract,
    }),
    project(`${header}-001`, 'Management Fixed Fee', 'FixedPrice', 'MGMT_FF', {
      contractId: contract,
      parentProjectId: header,
    }),
    project(`${header}-002`, 'Management Incentive Fee', 'FixedPrice', 'MGMT_KPI', {
      contractId: contract,
      parentProjectId: header,
    }),
    project(`${header}-003`, 'Billing Adjustments', 'TimeAndMaterial', 'BILL_ADJ', {
      contractId: contract,
      parentProjectId: header,
    }),
    project(`${companyId}-P002`, 'Venue operations overhead', 'Internal', 'OPEX_INT'),
  ];
}

/**
 * People import rows for a seeded live venue (V001–V003): the staff the mock D365 seed
 * places there, in slot order. Null for any other company. Used where a provisioned
 * venue has no import in the app (readiness plans for the sample venues).
 */
export function seededVenuePeopleRows(
  companyId: string,
  asOf: Date,
): NonNullable<DemoRequestSeed['peopleImport']>['rows'] | null {
  const staff = VENUE_SLOTS.map((slot) => MOCK_PEOPLE.find((p) => p.placement === `${companyId}:${slot}`));
  if (staff.some((p) => !p)) return null;
  return demoPeopleRows(companyId, asOf, staff as MockPerson[]);
}

/**
 * The demo's ledger overlays (overlays.ts), the same set for every venue that uses them: one
 * definition reused venue after venue, each applied to that venue's company only.
 * - B: journal RESTRICT may post only to cash accounts 110100–110199 (posting to 600500 is blocked)
 * - C: 510360 and 618160 suspended, default BusinessUnit 002 on 600500 (VERIFY 002 exists)
 */
export const DEMO_OVERLAY_ANSWERS = {
  'ledger.journals': [
    { name: 'RESTRICT', description: 'Restricted cash journal', fromAccount: '110100', toAccount: '110199' },
  ],
  'ledger.accountOverrides': [
    { mainAccountId: '510360', suspend: true, defaultDimensionValue: '' },
    { mainAccountId: '618160', suspend: true, defaultDimensionValue: '' },
    { mainAccountId: '600500', suspend: false, defaultDimensionValue: '002' },
  ],
  'ledger.overlayReason':
    'Venue cash is posted through its own controlled journal; two template accounts are not used at this venue.',
} as const;

/** Intake answers for one RSM test venue (testVenues.ts); dates relative to `asOf`. */
/**
 * The same overlays for Legends' 1100 (TRN): RESTRICT on restricted cash 108000–108900; two
 * intercompany accounts the venue doesn't use (201020 ASM Buyer, 201030 ASM Holdco I) suspended;
 * Payroll Billback 714900 defaulted to ServiceCategory S1100 instead of 1100's S1000.
 */
export const DEMO_OVERLAY_ANSWERS_1100 = {
  'ledger.journals': [
    { name: 'RESTRICT', description: 'Restricted cash journal', fromAccount: '108000', toAccount: '108900' },
  ],
  'ledger.accountOverrides': [
    { mainAccountId: '201020', suspend: true, defaultDimensionValue: '' },
    { mainAccountId: '201030', suspend: true, defaultDimensionValue: '' },
    { mainAccountId: '714900', suspend: false, defaultDimensionValue: 'S1100' },
  ],
  'ledger.overlayReason':
    'Venue cash is posted through its own controlled journal; two intercompany accounts are not used at this venue; venue payroll billbacks carry their own service category.',
} as const;

/** The demo overlays for a template company (1100 in the Legends TRN, USMF otherwise). */
export const demoOverlayAnswers = (templateCompany = 'USMF') =>
  templateCompany === '1100' ? DEMO_OVERLAY_ANSWERS_1100 : DEMO_OVERLAY_ANSWERS;

export function testVenueAnswers(
  v: TestVenue,
  asOf: Date,
  templateCompany = 'USMF',
): Record<string, unknown> {
  const n = v.companyId.replace(/\D/g, '').padStart(4, '0');
  return {
    'venue.name': `${v.companyId} - ${v.city}`,
    'venue.contractRef': `RSM-2026-${n}`,
    'venue.contractSignedDate': isoDate(asOf, -7),
    'venue.targetGoLive': isoDate(asOf, 45),
    'venue.type': v.venueType,
    'venue.modules': ['Procurement', 'AP'],
    'entity.id': v.companyId,
    'entity.legalName': `RSM ${v.city} Venue Services LLC`,
    'entity.searchName': `RSM ${v.code}`.slice(0, 20),
    'entity.address': { street: v.street, city: v.city, state: v.state, zip: v.zip },
    'entity.timeZone': v.timeZone,
    'entity.ein': `**-***${n}`,
    'finance.templateId': 'LTPL',
    'finance.venueDimensionValue': v.code,
    ...demoOverlayAnswers(templateCompany),
    'procurement.vendorCopyMode': 'All',
    'procurement.matchingPolicy': 'ThreeWay',
  };
}

/** The five directory people who staff a test venue, in slot order. */
export function testVenueStaff(v: TestVenue): MockPerson[] {
  return v.staff.map(([first, last]) => {
    const p = MOCK_PEOPLE.find((x) => x.firstName === first && x.lastName === last);
    if (!p) throw new Error(`${first} ${last} (${v.companyId}) is not in the mock directory`);
    return p;
  });
}

/** Company ID of the example request file; change it in the file before each live run. */
export const EXAMPLE_REQUEST_COMPANY = 'T002';

/**
 * Intake answers for the example request file (New request → Download example).
 * Fictional venue; dates relative to `asOf`; EIN in the masked form the app stores.
 * Answers left out (finance defaults, workflows) are filled from the template.
 */
export function exampleRequestAnswers(
  asOf: Date,
  companyId = EXAMPLE_REQUEST_COMPANY,
  templateCompany = 'USMF',
): Record<string, unknown> {
  return {
    'venue.name': 'Legends - Harborview Arena',
    'venue.contractRef': 'LG-2026-0602',
    'venue.contractSignedDate': isoDate(asOf, -7),
    'venue.targetGoLive': isoDate(asOf, 45),
    'venue.type': 'Arena',
    'venue.modules': ['Procurement', 'AP'],
    'entity.id': companyId,
    'entity.legalName': 'Harborview Arena Venue Services LLC',
    'entity.searchName': 'HARBORVIEW ARENA',
    'entity.address': { street: '200 Harbor Point Drive', city: 'Charleston', state: 'SC', zip: '29401' },
    'entity.timeZone': 'America/New_York',
    'entity.ein': '**-***2208',
    'finance.templateId': 'LTPL',
    'finance.venueDimensionValue': 'HARBORVIEW',
    ...demoOverlayAnswers(templateCompany),
    'procurement.vendorCopyMode': 'All',
    'procurement.matchingPolicy': 'ThreeWay',
  };
}

export function demoRequests(asOf: Date): DemoRequestSeed[] {
  return [
    {
      legalEntityId: 'V044',
      venueName: 'Legends - Canyon Amphitheater',
      status: 'Draft',
      currentGate: 1,
      requesterUpn: 'riley.chen@legends.example',
      targetGoLive: isoDate(asOf, 60),
      answers: {
        'venue.name': 'Legends - Canyon Amphitheater',
        'venue.type': 'Other',
        'entity.id': 'V044',
      },
      peopleImport: null,
    },
    {
      legalEntityId: 'V045',
      venueName: 'Legends - Bayfront Ballpark',
      status: 'InReview',
      currentGate: 2,
      requesterUpn: 'riley.chen@legends.example',
      targetGoLive: isoDate(asOf, 30),
      answers: {
        'venue.name': 'Legends - Bayfront Ballpark',
        'venue.contractRef': 'LG-2026-0451',
        'venue.contractSignedDate': isoDate(asOf, -12),
        'venue.targetGoLive': isoDate(asOf, 30),
        'venue.type': 'Stadium',
        'venue.modules': ['Procurement', 'AP', 'Projects'],
        'entity.id': 'V045',
        'entity.legalName': 'Bayfront Ballpark Venue Services LLC',
        'entity.searchName': 'BAYFRONT BALLPARK',
        'entity.address': { street: '1 Bayfront Way', city: 'Tampa', state: 'FL', zip: '33602' },
        'entity.timeZone': 'America/New_York',
        // Stored masked, as the app stores every EIN; synthetic.
        'entity.ein': '**-***4512',
        'finance.templateId': 'LTPL',
        'finance.currency': 'USD',
        'finance.venueDimensionValue': 'BAYFRONT',
        'procurement.vendorCopyMode': 'All',
        'procurement.matchingPolicy': 'ThreeWay',
      },
      peopleImport: { status: 'Valid', rows: demoPeopleRows('V045', asOf) },
    },
    {
      legalEntityId: 'V003',
      venueName: 'Legends - Lakeshore Convention Center',
      status: 'Ready',
      currentGate: 4,
      requesterUpn: 'riley.chen@legends.example',
      targetGoLive: isoDate(asOf, -20),
      answers: {
        'venue.name': 'Legends - Lakeshore Convention Center',
        'venue.type': 'Convention center',
        'entity.id': 'V003',
      },
      peopleImport: null,
    },
  ];
}
